import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useLocation, useMatches } from "react-router-dom";

import {
  breadcrumbMatches,
  resolveBreadcrumb,
  resolveRouteTitle,
  useLocalizeRouteTitle,
} from "@/components/layout/route-title";

import { PAGE_CONTEXT_LIMITS, PAGE_CONTEXT_PARAMS } from "../constants";
import type { IMayaPageContext } from "../types";

const clip = (value: string, max: number) =>
  value.length > max ? value.slice(0, max) : value;

/** Pathname plus only the navigation params (see PAGE_CONTEXT_PARAMS). */
export const pageContextPath = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  PAGE_CONTEXT_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) kept.set(key, value);
  });
  const query = kept.toString();
  return query ? `${pathname}?${query}` : pathname;
};

/**
 * The page open beside the Maya sidebar, described the way the header's
 * breadcrumbs describe it.
 *
 * `payload` is what the chat request carries — English route labels (the AI
 * reads them better than a translation) clipped to the API's limits, since an
 * over-long field would 400 the whole turn, and the path without its free-form
 * query string. `label` is the localized title the sidebar shows the user.
 */
export const useMayaPageContext = (): IMayaPageContext => {
  const { t } = useTranslation("common");
  const localizeTitle = useLocalizeRouteTitle();
  const { pathname, search } = useLocation();
  const matches = useMatches();

  const isCompany = pathname.startsWith("/company");
  const rootTitle = isCompany
    ? t("header.breadcrumb_dashboard")
    : t("header.breadcrumb_home");
  // English fallback for the payload; the localized one is only for display.
  const rootTitleEn = isCompany ? "Dashboard" : "Home";

  return useMemo(() => {
    const crumbs = breadcrumbMatches(matches).map(resolveBreadcrumb);
    const title = resolveRouteTitle(matches, rootTitleEn);
    const displayTitle = resolveRouteTitle(matches, rootTitle);

    const label =
      displayTitle === rootTitle ? rootTitle : localizeTitle(displayTitle);

    return {
      label,
      // The title usually repeats as the last crumb — don't show it twice.
      trail: crumbs
        .map(localizeTitle)
        .filter((c, i, all) => !(i === all.length - 1 && c === label)),
      payload: {
        path: clip(pageContextPath(pathname, search), PAGE_CONTEXT_LIMITS.path),
        title: clip(title, PAGE_CONTEXT_LIMITS.title),
        breadcrumbs: crumbs
          .slice(-PAGE_CONTEXT_LIMITS.breadcrumbs)
          .map((c) => clip(c, PAGE_CONTEXT_LIMITS.breadcrumb)),
      },
    };
  }, [matches, pathname, search, rootTitle, rootTitleEn, localizeTitle]);
};
